import { isUndefined, negate, uniq } from "lodash"
import { Freight, isCargoFreight, Shipment, ShipmentStatus, TransportOrder, TransportOrderType } from "@/model/transportorder/transportOrder"
import moment from "moment"
import { shipmentType, Volume, TransportationReport, Shipment as ReportShipment } from "@/model/transportation-report/transportation-report"
import { DeepPartial } from "@/redux/transportation-report.slice"

const uniqNonUndefined = (arr: (string | undefined)[]) => uniq(arr.filter(negate(isUndefined)))

const getFirstUniq = (arr: (string | undefined)[]) => {
    if (uniqNonUndefined(arr).length === 1) {
        return arr[0]
    }
}

const getFreightForwarderDuns = (shipments: Shipment[]): string | undefined => 
    getFirstUniq(shipments.map(shipment => shipment.freightForwarder.duns))

const getFreightForwarderSupplierCode = (shipments: Shipment[]): string | undefined =>
    getFirstUniq(shipments.map(shipment => shipment.freightForwarder.supplierCode))

const getBorderoNumber = (shipments: Shipment[]): string | undefined => 
    getFirstUniq(shipments.map(shipment => shipment.confirmationDraft.loadId))

const getAccessCode = (shipments: Shipment[], orderType: TransportOrderType): string | undefined => {
    let accessCodes: (string | undefined)[]

    if (orderType === TransportOrderType.MATERIAL) {
        accessCodes = shipments.map(shipment =>
            shipment.isEndOfTransportChain ? (shipment.confirmationDraft.accessCodeAtUnloading ?? shipment.endOfTransportChain.accessCode) : undefined
        )
    } else {
        accessCodes = shipments.map(shipment =>
            shipment.isStartOfTransportChain ? (shipment.confirmationDraft.accessCodeAtLoading ?? shipment.startOfTransportChain.accessCode) : undefined
        )
    }

    return getFirstUniq(accessCodes)
}

const getLoadedAt = (shipments: Shipment[]): string | undefined => {
    const dates = shipments
        .map(shipment => shipment.confirmationDraft.loadingTimeWindow?.time_from
            ?? shipment.currentTransportLeg.departureRequestedAt?.time_from
            ?? (shipment.isStartOfTransportChain ? shipment.startOfTransportChain.pickupRequestedAt.time_from : undefined)
        )
        .filter(loadingTime => loadingTime !== undefined)
        .map(loadingTime => moment(loadingTime))

    if (dates.length === 0) return

    return dates.reduce((minLoadingTime, currentLoadingTime) =>
        minLoadingTime <= currentLoadingTime ? minLoadingTime : currentLoadingTime
    ).toISOString()
} 

const getUnloadedAt = (shipments: Shipment[]): string | undefined => {
    const dates = shipments
        .map(shipment => shipment.confirmationDraft.unloadingTimeWindow?.time_to
            ?? shipment.currentTransportLeg.arrivalEstimatedAt?.time_to
            ?? (shipment.isEndOfTransportChain ? shipment.endOfTransportChain.deliveryEstimatedAt?.time_to : undefined)
        )
        .filter(unloadingTime => unloadingTime !== undefined)
        .map(unloadingTime => moment(unloadingTime))

    if (dates.length === 0) return

    return dates.reduce((maxUnloadingTime, currentUnloadingTime) =>
        maxUnloadingTime >= currentUnloadingTime ? maxUnloadingTime : currentUnloadingTime
    ).toISOString()
}

const getLoadingDuns = (shipments: Shipment[]): string | undefined =>
    getFirstUniq(
        shipments.map(
            shipment => shipment.currentTransportLeg.placeOfDeparture.externalIdentifiers.duns ??
                (shipment.isStartOfTransportChain ? shipment.startOfTransportChain.consignor.externalIdentifiers.duns : undefined)
        )
    )

const getUnloadingDuns = (shipments: Shipment[]): string | undefined =>
    getFirstUniq(
        shipments.map(
            shipment => shipment.currentTransportLeg.placeOfArrival.externalIdentifiers.duns ??
                (shipment.isEndOfTransportChain ? shipment.endOfTransportChain.consignee.externalIdentifiers.duns : undefined)
        )
    )

const getLoadingSupplierCode = (shipments: Shipment[]): string | undefined =>
    getFirstUniq(
        shipments.map(
            shipment => shipment.currentTransportLeg.placeOfDeparture.externalIdentifiers.supplierCode ??
                (shipment.isStartOfTransportChain ? shipment.startOfTransportChain.consignor.externalIdentifiers.supplierCode : undefined)
        )
    )

const getUnloadingSupplierCode = (shipments: Shipment[]): string | undefined =>
    getFirstUniq(
        shipments.map(
            shipment => shipment.currentTransportLeg.placeOfArrival.externalIdentifiers.supplierCode ??
                (shipment.isEndOfTransportChain ? shipment.endOfTransportChain.consignee.externalIdentifiers.supplierCode : undefined)
        )
    )

const getLicensePlate = (shipments: Shipment[]): string | undefined =>
    getFirstUniq(shipments.map(shipment => shipment.confirmationDraft.asset?.licensePlate))

const getVolume = (freight: Freight): Volume | null => {
    if (!isCargoFreight(freight)) return null

    const cargoFreight = freight

    if (cargoFreight.handling_units.length === 0) return null

    let value = 0
    for (const { dimension, quantity } of cargoFreight.handling_units) {
        if (!dimension 
            || !quantity 
            || !dimension.height?.value
            || !dimension.width?.value
            || !dimension.length?.value
        ) {
            return null
        }
        value += dimension.width.value * dimension.height.value * dimension.length.value * quantity
    }

    return {
        value,
        unit: "CUBIC_METER"
    }
}

const orderTypeToShipmentTypeMap: Record<TransportOrderType, typeof shipmentType[number]> = {
    [TransportOrderType.MATERIAL]: "MATERIALS",
    [TransportOrderType.EMPTIES]: "EMPTIES",
}

const getShipments = (shipments: Shipment[], orderType: TransportOrderType): ReportShipment[] =>
    shipments.map(shipment => { 
        const shipmentType = orderTypeToShipmentTypeMap[orderType]
        if (shipmentType === "MATERIALS") {
            return {
                type: "MATERIALS",
                shipFrom: {
                    duns: shipment.startOfTransportChain.consignor.externalIdentifiers.duns || "",
                    supplierCode: shipment.startOfTransportChain.consignor.externalIdentifiers.supplierCode || "",
                },
                shipTo: {
                    duns: shipment.endOfTransportChain.consignee.externalIdentifiers.duns || "",
                    plantCode: shipment.endOfTransportChain.consignee.externalIdentifiers.plantCode || "",
                },
                shipmentNumber: shipment.external_consignment_reference_number || "",
                volume: getVolume(shipment.freight)
            }
        }

        return {
            type: "EMPTIES",
            shipFrom: {
                duns: shipment.startOfTransportChain.consignor.externalIdentifiers.duns || "",
                supplierCode: shipment.startOfTransportChain.consignor.externalIdentifiers.supplierCode || "",
            },
            shipTo: {
                duns: shipment.endOfTransportChain.consignee.externalIdentifiers.duns || "",
                supplierCode: shipment.endOfTransportChain.consignee.externalIdentifiers.supplierCode || "",
            },
            shipmentNumber: shipment.external_consignment_reference_number || "",
            volume: getVolume(shipment.freight)
        }
    })


export const transportOrderToTransportationReport = (transportOrder: TransportOrder): DeepPartial<TransportationReport> => {
    const shipments = transportOrder._embedded?.shipments.filter(
        shipment => shipment.status !== ShipmentStatus.CANCELLED && isCargoFreight(shipment.freight)
    )
    if (!shipments || shipments.length === 0) throw Error("No shipments data")

    return {
        transportDetails: {
            accessCode: getAccessCode(shipments, transportOrder.orderType),
            freightForwarder: {
                duns: getFreightForwarderDuns(shipments),
                supplierCode: getFreightForwarderSupplierCode(shipments),
            },
            borderoNumber: getBorderoNumber(shipments),
            loading: {
                loadedAt: getLoadedAt(shipments),
                duns: getLoadingDuns(shipments),
                supplierCode: getLoadingSupplierCode(shipments),
            },
            unloading: {
                unloadedAt: getUnloadedAt(shipments),
                duns: getUnloadingDuns(shipments),
                supplierCode: getUnloadingSupplierCode(shipments),
            },
            transportMode: {
                type: "ROAD",
                licensePlate: getLicensePlate(shipments),
            }
        },
        shipments: getShipments(shipments, transportOrder.orderType),
    }
}
